"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import Swal from "sweetalert2"
import StandCategoryService from "../../../Service/StandCategoryService"
import { toast } from "react-toastify"
import type { StandCategory } from "./types"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: StandCategory | null
}

const StandCategoryModal = ({ isOpen, toggle, onSuccess, initialData }: Props) => {
  const [form, setForm] = useState<Omit<StandCategory, "id">>({
    name: "",
    num_representatives: 0,
    num_cars: 0,
    status: false,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{
    name?: string
    num_representatives?: string
    num_cars?: string
  }>({})

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name,
        num_representatives: initialData.num_representatives,
        num_cars: initialData.num_cars,
        status: initialData.status,
      })
    } else {
      setForm({ name: "", num_representatives: 0, num_cars: 0, status: false })
    }
    // Clear errors when modal opens/closes or data changes
    setErrors({})
  }, [initialData, isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    
    let processedValue: any = type === "checkbox" ? checked : value
    
    // Convert numeric fields to numbers
    if (name === "num_representatives" || name === "num_cars") {
      processedValue = value === "" ? 0 : Number(value)
    }

    setForm({
      ...form,
      [name]: processedValue,
    })

    // Clear error for this field when user starts typing/changing
    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: {
      name?: string
      num_representatives?: string
      num_cars?: string
    } = {}

    // Name validation
    if (!form.name.trim()) {
      newErrors.name = "Name is required"
    } else if (form.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters long"
    } else if (form.name.trim().length > 100) {
      newErrors.name = "Name must not exceed 100 characters"
    } else if (!/^[a-zA-Z0-9\s\-_&().,]+$/.test(form.name.trim())) {
      newErrors.name = "Name contains invalid characters"
    }

    // Staff validation
    if (form.num_representatives < 0) {
      newErrors.num_representatives = "Badges number cannot be negative"
    } else if (form.num_representatives > 1000) {
      newErrors.num_representatives = "Badges number cannot exceed 1000"
    } else if (!Number.isInteger(form.num_representatives)) {
      newErrors.num_representatives = "Badges number must be a whole number"
    }

    // Cars validation
    if (form.num_cars < 0) {
      newErrors.num_cars = "Car count cannot be negative"
    } else if (form.num_cars > 100) {
      newErrors.num_cars = "Car count cannot exceed 100"
    } else if (!Number.isInteger(form.num_cars)) {
      newErrors.num_cars = "Car count must be a whole number"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setForm({ name: "", num_representatives: 0, num_cars: 0, status: false })
    setErrors({})
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors")
      return
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${initialData ? "update" : "create"} this Stand Category?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${initialData ? "update" : "create"} it!`,
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      setLoading(true)

      const formData = {
        name: form.name.trim(),
        num_representatives: Number(form.num_representatives),
        num_cars: Number(form.num_cars),
        status: form.status,
      }

      if (initialData) {
        await StandCategoryService.update(initialData.id, formData)

        await Swal.fire({
          title: "Updated!",
          text: "Stand Category has been updated successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })
      } else {
        await StandCategoryService.create(formData)

        await Swal.fire({
          title: "Created!",
          text: "Stand Category has been created successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })

        // Reset form after successful creation
        resetForm()
      }

      onSuccess() // refetch
      toggle()
    } catch (error: any) {
      console.error("Stand Category operation error:", error)

      let errorMessage = "Something went wrong. Please try again."

      // Handle specific error cases
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.name) {
        errorMessage = Array.isArray(error.response.data.name) ? error.response.data.name[0] : error.response.data.name
      } else if (error.response?.data?.non_field_errors) {
        errorMessage = Array.isArray(error.response.data.non_field_errors) 
          ? error.response.data.non_field_errors[0] 
          : error.response.data.non_field_errors
      }

      await Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleModalToggle = () => {
    if (!initialData) {
      resetForm()
    }
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleModalToggle}>
      <ModalHeader toggle={handleModalToggle}>
        {initialData ? "Edit" : "Add"} Stand Category
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="name">
            Name <span className="text-danger">*</span>
          </Label>
          <Input
            name="name"
            value={form.name}
            onChange={handleChange}
            invalid={!!errors.name}
            placeholder="Enter stand category name"
            maxLength={100}
          />
          {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
          <small className="text-muted">{form.name.length}/100 characters</small>
        </FormGroup>

        <FormGroup>
          <Label for="num_representatives">
            Number of badges <span className="text-danger">*</span>
          </Label>
          <Input
            type="number"
            name="num_representatives"
            value={form.num_representatives}
            onChange={handleChange}
            invalid={!!errors.num_representatives}
            min="0"
            max="1000"
            placeholder="Enter number of staff"
          />
          {errors.num_representatives && <FormFeedback>{errors.num_representatives}</FormFeedback>}
          <small className="text-muted">Maximum allowed badges for this category</small>
        </FormGroup>

        <FormGroup>
          <Label for="num_cars">
            Car Count <span className="text-danger">*</span>
          </Label>
          <Input
            type="number"
            name="num_cars"
            value={form.num_cars}
            onChange={handleChange}
            invalid={!!errors.num_cars}
            min="0"
            max="100"
            placeholder="Enter number of cars"
          />
          {errors.num_cars && <FormFeedback>{errors.num_cars}</FormFeedback>}
          <small className="text-muted">Maximum allowed cars for this category</small>
        </FormGroup>

        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              name="status"
              checked={form.status}
              onChange={handleChange}
            />
            <span className="ms-2">Active Status</span>
          </Label>
          <small className="text-muted d-block mt-1">
            {form.status ? "This category is active and available for use" : "This category is inactive"}
          </small>
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="secondary" onClick={handleModalToggle} className="me-2" disabled={loading}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleSubmit} 
            disabled={loading || !form.name.trim()}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" />
                {initialData ? "Updating..." : "Creating..."}
              </>
            ) : initialData ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default StandCategoryModal